/**
 * Initialize native Capacitor plugins (StatusBar, Keyboard, SplashScreen).
 * Safe to call on web — each plugin import is guarded by a native platform check.
 *
 * SplashScreen is NOT hidden here — call hideSplashScreen() from App.jsx
 * once auth loading is complete, so users see the splash until the app is ready.
 */

/**
 * Find the nearest scrollable ancestor of an element.
 */
function findScrollContainer(el) {
  let node = el?.parentElement
  while (node && node !== document.body) {
    const style = getComputedStyle(node)
    if (
      (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node
    }
    node = node.parentElement
  }
  return null
}

/**
 * Scroll the currently focused input so it sits just below any sticky headers
 * in its scroll container. Works for both .main-content and .modal-content.
 */
function scrollFocusedInputIntoView() {
  const focused = document.activeElement
  if (!focused || !['INPUT', 'TEXTAREA', 'SELECT'].includes(focused.tagName)) return

  const container = findScrollContainer(focused)
  if (!container) return

  const containerRect = container.getBoundingClientRect()
  let stickyBottom = containerRect.top

  // Query sticky elements inside the container
  container.querySelectorAll('[data-sticky], .modal-header').forEach(el => {
    const rect = el.getBoundingClientRect()
    if (rect.bottom > stickyBottom && rect.top < containerRect.top + 200) {
      stickyBottom = Math.max(stickyBottom, rect.bottom)
    }
  })

  // For .main-content, also account for the external .app-header
  if (container.classList.contains('main-content')) {
    const appHeader = document.querySelector('.app-header')
    if (appHeader) {
      const rect = appHeader.getBoundingClientRect()
      stickyBottom = Math.max(stickyBottom, rect.bottom)
    }
  }

  const gap = focused.getBoundingClientRect().top - stickyBottom
  container.scrollTop += gap - 8
}

export async function initNativePlugins() {
  const isNative = typeof window !== 'undefined' &&
    window.Capacitor?.isNativePlatform?.()

  if (!isNative) return

  try {
    const [
      { StatusBar, Style },
      { Keyboard },
    ] = await Promise.all([
      import('@capacitor/status-bar'),
      import('@capacitor/keyboard'),
    ])

    // Match status bar to the app's dark theme default
    StatusBar.setStyle({ style: Style.Dark }).catch(() => {})

    // On Android, overlay the status bar so CSS env(safe-area-inset-top) works
    if (window.Capacitor.getPlatform() === 'android') {
      StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {})
      StatusBar.setBackgroundColor({ color: '#00000000' }).catch(() => {})
      document.documentElement.classList.add('platform-android')
    }

    // Don't resize viewport when keyboard appears — we handle it via CSS variable
    Keyboard.setResizeMode?.({ mode: 'none' }).catch(() => {})

    // Track keyboard height via visualViewport for continuous updates (handles
    // swipe-to-dismiss and partial keyboard states). Capacitor events are used
    // as supplementary triggers for scroll-into-view.
    let nativeKeyboardOpen = false

    if (window.visualViewport) {
      const updateKbHeight = () => {
        const kbHeight = window.innerHeight - window.visualViewport.height - window.visualViewport.offsetTop
        if (kbHeight > 100) {
          document.documentElement.style.setProperty('--keyboard-height', `${kbHeight}px`)
          document.documentElement.classList.add('keyboard-visible')
          nativeKeyboardOpen = true
        } else {
          document.documentElement.style.setProperty('--keyboard-height', '0px')
          document.documentElement.classList.remove('keyboard-visible')
          nativeKeyboardOpen = false
        }
      }
      window.visualViewport.addEventListener('resize', updateKbHeight)
      window.visualViewport.addEventListener('scroll', updateKbHeight)
    }

    Keyboard.addListener('keyboardDidShow', () => {
      // visualViewport handles the height; just trigger scroll adjustment
      setTimeout(() => scrollFocusedInputIntoView(), 120)
    })

    Keyboard.addListener('keyboardDidHide', () => {
      // Ensure cleanup in case visualViewport didn't fire
      if (nativeKeyboardOpen) {
        document.documentElement.style.setProperty('--keyboard-height', '0px')
        document.documentElement.classList.remove('keyboard-visible')
        nativeKeyboardOpen = false
      }
    })
  } catch (err) {
    // Lazy import to avoid circular dependency at module load time
    import('../utils/logger').then(({ default: logger }) => {
      logger.warn('Native plugin init failed:', err)
    }).catch(() => console.warn('Native plugin init failed:', err))
  }
}

/**
 * Web keyboard detection using visualViewport API.
 * Sets --keyboard-height CSS variable so action buttons follow the keyboard.
 */
export function initWebKeyboardHandling() {
  const isNative = typeof window !== 'undefined' &&
    window.Capacitor?.isNativePlatform?.()

  if (isNative) return
  if (typeof window === 'undefined' || !window.visualViewport) return

  let keyboardOpen = false

  window.visualViewport.addEventListener('resize', () => {
    const vv = window.visualViewport
    const keyboardHeight = window.innerHeight - vv.height - vv.offsetTop

    const focused = document.activeElement
    const isInputFocused = focused && ['INPUT', 'TEXTAREA', 'SELECT'].includes(focused.tagName)

    if (keyboardHeight > 100 && isInputFocused) {
      document.documentElement.style.setProperty('--keyboard-height', `${keyboardHeight}px`)
      document.documentElement.classList.add('keyboard-visible')
      if (!keyboardOpen) {
        keyboardOpen = true
        setTimeout(() => scrollFocusedInputIntoView(), 100)
      }
    } else {
      if (keyboardOpen) {
        keyboardOpen = false
      }
      document.documentElement.style.setProperty('--keyboard-height', '0px')
      document.documentElement.classList.remove('keyboard-visible')
    }
  })
}

/**
 * Hide the native splash screen. Call this once the app is ready
 * (auth resolved, first meaningful paint).
 */
export async function hideSplashScreen() {
  const isNative = typeof window !== 'undefined' &&
    window.Capacitor?.isNativePlatform?.()

  if (!isNative) return

  try {
    const { SplashScreen } = await import('@capacitor/splash-screen')
    SplashScreen.hide({ fadeOutDuration: 300 })
  } catch (err) {
    import('../utils/logger').then(({ default: logger }) => {
      logger.warn('SplashScreen hide failed:', err)
    }).catch(() => console.warn('SplashScreen hide failed:', err))
  }
}
